import React, { useEffect, useState, useCallback } from "react";
import { RefreshCw, Sparkles, X } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

const POLL_INTERVAL_MS = 60 * 1000; // 60s when tab is active
const STORAGE_KEY = "hosys_backend_version";
const DISMISS_KEY_PREFIX = "hosys_version_dismissed_";

export default function VersionWatcher() {
  const { lang } = useI18n();
  const [newVersion, setNewVersion] = useState(null);
  const tr = (ca, es) => (lang === "es" ? es : ca);

  const check = useCallback(async () => {
    try {
      const r = await api.get("/version");
      const v = r.data?.version;
      if (!v) return;
      const known = localStorage.getItem(STORAGE_KEY);
      if (!known) {
        localStorage.setItem(STORAGE_KEY, v);
        return;
      }
      if (known !== v) {
        // Don't re-prompt if the user dismissed this specific version.
        const dismissed = localStorage.getItem(DISMISS_KEY_PREFIX + v);
        if (!dismissed) setNewVersion(v);
      }
    } catch {
      // network errors are not interesting here
    }
  }, []);

  useEffect(() => {
    check();
    const id = setInterval(check, POLL_INTERVAL_MS);
    // Re-check when the tab is brought back to focus
    const onVis = () => { if (!document.hidden) check(); };
    window.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", check);
    // Listen to the custom event fired by api.js when X-App-Version changes
    const onCustom = (e) => {
      const v = e.detail?.current;
      if (!v) return;
      const dismissed = localStorage.getItem(DISMISS_KEY_PREFIX + v);
      if (!dismissed) setNewVersion(v);
    };
    window.addEventListener("hosys:new-version", onCustom);
    return () => {
      clearInterval(id);
      window.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", check);
      window.removeEventListener("hosys:new-version", onCustom);
    };
  }, [check]);

  if (!newVersion) return null;

  const reload = () => {
    // Update the stored version BEFORE reloading so we don't show the banner
    // again immediately after the reload.
    try {
      localStorage.setItem(STORAGE_KEY, newVersion);
    } catch {}
    // Force a hard reload to bypass cache for HTML / chunks.
    window.location.reload();
  };

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY_PREFIX + newVersion, "1");
    } catch {}
    setNewVersion(null);
  };

  return (
    <div
      data-testid="version-watcher-banner"
      className="fixed bottom-5 right-5 z-[120] max-w-sm rounded-xl border border-blue-500/50 bg-gradient-to-br from-blue-600 to-indigo-700 shadow-[0_20px_60px_-15px_rgba(59,130,246,0.6)] backdrop-blur-md text-white animate-in slide-in-from-bottom-4 fade-in duration-500">
      <div className="p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
          <Sparkles size={16} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-sm">
            {tr("Hi ha una nova versió disponible",
                "Hay una nueva versión disponible")}
          </h3>
          <p className="text-xs text-blue-100/90 leading-relaxed mt-1">
            {tr(
              "Recarrega la pàgina per obtenir les últimes millores. Els teus canvis no es perdran.",
              "Recarga la página para obtener las últimas mejoras. Tus cambios no se perderán."
            )}
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={reload}
              className="px-3 py-1.5 rounded-lg bg-white text-blue-700 text-xs font-semibold hover:bg-blue-50 transition flex items-center gap-1.5"
              data-testid="version-watcher-reload">
              <RefreshCw size={12} />
              {tr("Actualitzar ara", "Actualizar ahora")}
            </button>
            <button
              onClick={dismiss}
              className="text-xs text-blue-100/70 hover:text-white transition"
              data-testid="version-watcher-dismiss">
              {tr("Més tard", "Más tarde")}
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-blue-100/60 hover:text-white transition flex-shrink-0"
          aria-label="dismiss"
          data-testid="version-watcher-close">
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
